export const ELLIPSIS_MAX_CHAR_LENGTH = 15;

export function ellipsis(
	str: string,
	maxCharLength = ELLIPSIS_MAX_CHAR_LENGTH,
	dotsPosition?: "start" | "center" | "end",
) {
	const dotsPos = dotsPosition || "end";

	if (str.length > maxCharLength) {
		const dots = "...";
		const charsToShow = maxCharLength - dots.length;
		let frontChars = Math.ceil(charsToShow / 2);
		let backChars = Math.floor(charsToShow / 2);

		if (dotsPos === "start") {
			frontChars = 0;
			backChars = charsToShow;
		} else if (dotsPos === "end") {
			frontChars = charsToShow;
			backChars = 0;
		}

		return (
			str.substring(0, frontChars) +
			dots +
			str.substring(str.length - backChars)
		);
	}
	return str;
}

export function toTitleCase(str = "") {
	return str
		.replace(/\w\S*/g, (txt) => {
			return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
		})
		.replaceAll("_", " ");
}

export function formatIDR(value?: number | null, minimumFractionDigits = 0) {
	return new Intl.NumberFormat("id-ID", {
		minimumFractionDigits,
	}).format(Number(value ?? 0));
}

// Same as formatIDR but with an explicit "Rp" prefix, for amounts that need an
// unambiguous currency marker (finance/vendor-facing money columns and cards).
export function formatRupiah(value?: number | null, minimumFractionDigits = 0) {
	return `Rp ${formatIDR(value, minimumFractionDigits)}`;
}

export function formatThousand(value?: number | null) {
	return new Intl.NumberFormat("id-ID", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(Number(value ?? 0));
}

// Nominal amount with exactly two decimals, id-ID style and no currency marker,
// e.g. 1000 -> "1.000,00". Extra decimals are truncated (not rounded), so
// 5191.887 -> "5.191,88". Use for money columns that display the bare number
// (e.g. printed documents).
export function formatAmount(value?: number | null) {
	// Multiply-then-truncate drifts on floats (e.g. 5126605.52 * 100 =
	// 512660551.99999994), which would wrongly drop a cent. Round the scaled
	// value at the 4th decimal to clear the noise before truncating.
	const scaled = Number((Number(value ?? 0) * 100).toFixed(4));
	const truncated = Math.trunc(scaled) / 100;
	return new Intl.NumberFormat("id-ID", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(truncated);
}

// Same as formatAmount but with an explicit "Rp" prefix, e.g. "Rp 1.000,00".
export function formatAmountRupiah(value?: number | null) {
	return `Rp ${formatAmount(value)}`;
}

// Like formatAmount but rounds the last decimal instead of truncating, so
// 5191.887 -> "5.191,89". Use for values that are meant to be rounded
// (e.g. discount amounts).
export function formatAmountRounded(value?: number | null) {
	return new Intl.NumberFormat("id-ID", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(Number(value ?? 0));
}

// Same as formatAmountRounded but with an explicit "Rp" prefix.
export function formatAmountRoundedRupiah(value?: number | null) {
	return `Rp ${formatAmountRounded(value)}`;
}
